import type React from 'react';
import { CircleDot } from 'lucide-react';
import { getDisciplines, type DisciplineChildType } from './disciplines';
import { OCCUPATIONAL_PATHS, OCCUPATIONAL_PATH_NAMES } from './occupationalPaths';
import { AVOCATIONAL_PATHS, AVOCATIONAL_PATH_NAMES } from './avocationalPaths';
import { INTELLECTUAL_PATHS, INTELLECTUAL_PATH_NAMES } from './intellectualPaths';
import { EXISTENTIAL_PILLARS } from './existentialPillars';
import { RELATIONSHIP_CATEGORIES, CATEGORY_COLORS } from './relationshipTypes';

/**
 * Areas — the generic, domain-agnostic layer on top of the discipline registry
 * (Phase B of the area model). An "area" is a domain sub-node an action can be
 * tagged to; its rank is a derived 10-Rank view of the economy XP its tagged
 * actions already earned (see `utils/areaRank` + `utils/areaAttribution`).
 *
 * For Personal the areas ARE the 7 disciplines (the proof). Every domain also
 * gets a **General** catch-all so the area picker works everywhere and
 * domain-level work that doesn't fit a named area still has a home.
 *
 * ⭐⭐ **ALL SEVEN DOMAINS NOW EXPOSE THEIR OWN AREAS** (owner, 2026-08-19) —
 * this file's own note promised "a later pass", and this is it. Until then only
 * `personal` and `ecological` had entries in `DISCIPLINES_BY_DOMAIN`, so the
 * other five offered nothing but General: the picker looked broken in five
 * domains out of seven, on every action type.
 *
 * ⚠️⚠️ **EACH DOMAIN'S TAXONOMY ALREADY EXISTED — it simply was not wired here.**
 * Occupational/Avocational/Intellectual Paths, Existential Pillars and the
 * Social sphere taxonomy are the SAME lists their own pages render, so an area
 * tagged from a form is the area shown on the domain page. Do not invent a
 * parallel list for a domain; import its existing one.
 *
 * ⚠️⚠️ **THE AREA ID IS A STORAGE KEY.** It is persisted in
 * `DisciplineAttachment.disciplineId` on every tagged action. For the Path and
 * Pillar domains the id is the path NAME, because that is already what their
 * own tables store (`career_roles.primary_career_path`). Renaming one orphans
 * every action tagged to it. [[display-name-is-a-storage-key]]
 */

/** The id of every domain's catch-all area. */
export const GENERAL_AREA_ID = 'general';

/**
 * The only tags a Social ACTION may carry: the four relationship spheres plus
 * General. ⚠️ Never one of Social's seven pages (Belonging, Engagement…) —
 * those are navigation, not attribution (scope 007).
 */
export const SOCIAL_TAGS: readonly string[] = [...RELATIONSHIP_CATEGORIES, GENERAL_AREA_ID];

export interface AreaDefinition {
  /** Unique within its domain. A discipline id, or `GENERAL_AREA_ID`. */
  id: string;
  domainId: string;
  name: string;
  /** Accent color for chips. Falls back to the domain color at the call site. */
  accent?: string;
  icon?: React.ElementType;
  /** What the area's children are — drives the Fitness param sub-picker. */
  childType: DisciplineChildType;
}

/** The areas an action in this domain can be tagged to (named ones + General). */
/** A named area from one of the per-domain taxonomies. */
function named(
  domainId: string,
  name: string,
  accent?: string,
  icon?: React.ElementType,
): AreaDefinition {
  // ⚠️ `childType: 'leaf'` — only Personal's Fitness declares `parameters`,
  // which is what drives the major-parameter sub-picker. A Path is not a
  // parameter-backed area.
  return { id: name, domainId, name, accent, icon, childType: 'leaf' };
}

/**
 * The named areas of a domain, from whichever list that domain already owns.
 * ⚠️ `personal` and `ecological` come from the discipline registry; the rest
 * from their own constants. Adding a domain here means importing ITS list.
 */
function namedAreas(domainId: string): AreaDefinition[] {
  switch (domainId) {
    case 'occupational':
      return OCCUPATIONAL_PATH_NAMES.map(n =>
        named(domainId, n, OCCUPATIONAL_PATHS[n].color, OCCUPATIONAL_PATHS[n].icon));
    case 'avocational':
      return AVOCATIONAL_PATH_NAMES.map(n =>
        named(domainId, n, AVOCATIONAL_PATHS[n].color, AVOCATIONAL_PATHS[n].icon));
    case 'intellectual':
      return INTELLECTUAL_PATH_NAMES.map(n =>
        named(domainId, n, INTELLECTUAL_PATHS[n].color, INTELLECTUAL_PATHS[n].icon));
    case 'existential':
      // ⚠️ Pillar `icon` is a lucide NAME (a string), not a component, so it is
      // not passed through — `AreaDefinition.icon` expects an ElementType.
      return Object.values(EXISTENTIAL_PILLARS).map(pillar =>
        named(domainId, pillar.name, pillar.color));
    case 'social':
      // ⚠️ The 4-sphere relationship taxonomy (SO3), not the 7 parameters.
      return RELATIONSHIP_CATEGORIES.map(c => named(domainId, c, CATEGORY_COLORS[c]));
    default:
      return getDisciplines(domainId).map(d => ({
        id: d.id,
        domainId: d.domainId,
        name: d.name,
        accent: d.accent,
        icon: d.icon,
        childType: d.childType,
      }));
  }
}

export function getAreas(domainId: string): AreaDefinition[] {
  const disciplines = namedAreas(domainId);

  const general: AreaDefinition = {
    id: GENERAL_AREA_ID,
    domainId,
    name: 'General',
    icon: CircleDot,
    childType: 'leaf',
  };

  return [...disciplines, general];
}

/** A single area by domain + id. */
export function getArea(domainId: string, areaId: string): AreaDefinition | undefined {
  return getAreas(domainId).find(a => a.id === areaId);
}
